import React, { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight,
  Send,
  CheckCircle
} from "lucide-react";
import HeroOther from "../HeroOther/HeroOther";
import { useRegion } from "../../../regions/RegionContext";
import { trackFormSubmit } from "../../../lib/gtag";
import { sendTransactionalEmail, escapeHtml } from "../../../services/email";

const HeroOtherData = {
  id: 1,
  h1: "Get in touch with us",
  s1: "Contact Terces",
  c1: "We welcome your inquiries and feedback at Terces. As a process and technology solutions provider for the food and beverage industry, we are committed to serving our clients with excellence.",
  c2: "Our team is dedicated to helping you achieve your goals with our comprehensive suite of services.",
};

const Contact = () => {
  const { region, config } = useRegion();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (rawData) => {
    setLoading(true);
    // Everything below is interpolated into an HTML email, so escape it first.
    const data = Object.fromEntries(
      Object.entries(rawData).map(([key, value]) => [key, escapeHtml(value)])
    );
    try {
      await sendTransactionalEmail({
        replyTo: { email: rawData.email, name: `${data.first_name} ${data.last_name}` },
        subject: `New Contact Form Submission from ${data.first_name} ${data.last_name}`,
        htmlContent: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>New Contact Form Submission</title>
</head>
<body style="margin:0;padding:0;background-color:#f4f4f5;font-family:'Segoe UI',Arial,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f4f4f5;padding:40px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">

          <!-- Header -->
          <tr>
            <td style="background-color:#1b7840;padding:36px 40px;text-align:center;">
              <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;letter-spacing:0.5px;">Terces Solutions</h1>
              <p style="margin:8px 0 0;color:rgba(255,255,255,0.8);font-size:13px;">Contact Form Submission</p>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:36px 40px;">
              <p style="margin:0 0 24px;font-size:15px;color:#374151;line-height:1.6;">
                You have received a new message through the contact form on your website. Here are the details:
              </p>

              <!-- Info Cards -->
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="padding:0 0 12px;">
                    <table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;border-radius:8px;border-left:4px solid #1b7840;">
                      <tr>
                        <td style="padding:14px 18px;">
                          <p style="margin:0;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;">Full Name</p>
                          <p style="margin:4px 0 0;font-size:15px;color:#111827;font-weight:600;">${data.first_name} ${data.last_name}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:0 0 12px;">
                    <table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;border-radius:8px;border-left:4px solid #1b7840;">
                      <tr>
                        <td style="padding:14px 18px;">
                          <p style="margin:0;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;">Email Address</p>
                          <p style="margin:4px 0 0;font-size:15px;color:#1b7840;font-weight:600;">${data.email}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:0 0 12px;">
                    <table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;border-radius:8px;border-left:4px solid #1b7840;">
                      <tr>
                        <td style="padding:14px 18px;">
                          <p style="margin:0;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;">Phone Number</p>
                          <p style="margin:4px 0 0;font-size:15px;color:#111827;font-weight:600;">${data.phone}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="padding:0 0 0;">
                    <table width="100%" cellpadding="0" cellspacing="0" style="background:#f9fafb;border-radius:8px;border-left:4px solid #1b7840;">
                      <tr>
                        <td style="padding:14px 18px;">
                          <p style="margin:0;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:0.8px;">Message</p>
                          <p style="margin:8px 0 0;font-size:15px;color:#374151;line-height:1.7;white-space:pre-wrap;">${data.message}</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>

              <!-- CTA -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:28px;">
                <tr>
                  <td align="center">
                    <a href="mailto:${data.email}" style="display:inline-block;background-color:#1b7840;color:#ffffff;text-decoration:none;font-size:14px;font-weight:600;padding:13px 32px;border-radius:8px;">Reply to ${data.first_name}</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color:#f9fafb;border-top:1px solid #e5e7eb;padding:24px 40px;text-align:center;">
              <p style="margin:0;font-size:12px;color:#9ca3af;">This email was sent from the contact form at <strong>terces.io</strong></p>
              <p style="margin:6px 0 0;font-size:12px;color:#9ca3af;">© ${new Date().getFullYear()} Terces Solutions. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`,
      });

      trackFormSubmit("contact", { site_region: region });
      toast.success("Message sent successfully! We'll get back to you soon.", {
        duration: 3000,
        position: "top-right",
        style: { background: "#1b7840", color: "#fff" },
      });
      reset();
    } catch (error) {
      toast.error(
        "We couldn't send your message. Please email us at info@terces.io and we'll pick it up from there.",
        { duration: 5000, position: "top-right" }
      );
    } finally {
      setLoading(false);
    }
  };

  const locationInfo =
    region === "global"
      ? {
          icon: MapPin,
          title: "Our Location",
          content: config.address.lines.join(" "),
          link: "https://www.google.com/maps/place/Terces+Solutions+Pvt.+Ltd./@18.0199616,79.5482948,17z/data=!3m1!4b1!4m6!3m5!1s0x3a334f8bcbe9528b:0x42fbc9c650ed7a5d!8m2!3d18.0199565!4d79.5508697!16s%2Fg%2F11smyg0cy4?entry=ttu",
          linkText: "View on Google Maps",
          isExternal: true,
        }
      : {
          icon: MapPin,
          title: "Our Location",
          content: config.address.lines.join(" "),
          linkText: config.address.note,
        };

  const phoneInfo = config.contact.showPhone
    ? {
        icon: Phone,
        title: "Phone Number",
        content: config.contact.phoneDisplay,
        link: config.contact.phoneTel,
        linkText: "Call us now",
      }
    : {
        icon: Phone,
        title: "WhatsApp",
        content: "WhatsApp us",
        link: config.contact.whatsappLink,
        linkText: "Chat on WhatsApp",
        isExternal: true,
      };

  const contactInfo = [
    locationInfo,
    phoneInfo,
    {
      icon: Mail,
      title: "Email Address",
      content: "info@terces.io",
      link: "mailto:info@terces.io",
      linkText: "Send us an email",
    },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <>
      <HeroOther item={HeroOtherData} />
      
      <section className="relative bg-white py-20 md:py-28 px-6 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.015]">
          <div className="absolute top-0 left-0 w-full h-full" 
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
              backgroundSize: '48px 48px'
            }}
          />
        </div>

        {/* Decorative Blobs */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.03, scale: 1 }}
          transition={{ duration: 1.2 }}
          viewport={{ once: true }}
          className="absolute top-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 0.03, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.3 }}
          viewport={{ once: true }}
          className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
            
            {/* LEFT SIDE - Contact Info */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
            >
              {/* Introduction Text */}
              <motion.div variants={itemVariants} className="mb-12">
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                  Our team is dedicated to helping you achieve your goals with our
                  comprehensive suite of services. Contact us today to learn more
                  about how we can help you optimize your operations, streamline
                  your processes, and drive growth for your business. We look
                  forward to hearing from you.
                </p>
              </motion.div>

              {/* Contact Cards */}
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  
                  return (
                    <motion.a
                      key={index}
                      variants={itemVariants}
                      href={info.link}
                      target={info.isExternal ? "_blank" : undefined}
                      rel={info.isExternal ? "noopener noreferrer" : undefined}
                      whileHover={{ x: 5 }}
                      className="group flex items-start gap-5 p-6 bg-[#F6F4EF] rounded-2xl hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-primary/20"
                    >
                      {/* Icon Container */}
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className="flex-shrink-0 w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md group-hover:bg-primary group-hover:shadow-xl transition-all duration-300"
                      >
                        <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" strokeWidth={2} />
                      </motion.div>

                      {/* Content */}
                      <div className="flex-1 pt-1">
                        <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                          {info.title}
                        </h4>
                        <p className="text-base text-gray-600 mb-1">
                          {info.content}
                        </p>
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:gap-3 transition-all">
                          {info.linkText}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </motion.a>
                  );
                })}
              </div>

              {/* Additional Info */}
              <motion.div
                variants={itemVariants}
                className="mt-10 p-6 bg-gradient-to-br from-primary/5 to-transparent rounded-2xl border border-primary/10"
              >
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" strokeWidth={2} />
                  <div>
                    <h5 className="font-bold text-gray-900 mb-2">Quick Response Time</h5>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      We typically respond to all inquiries within 24 hours during business days.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT SIDE - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="relative bg-white p-8 md:p-10 rounded-3xl shadow-xl border-2 border-gray-100">
                {/* Form Header */}
                <div className="mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Send us a message
                  </h3>
                  <p className="text-gray-600">
                    Fill out the form below and we'll get back to you shortly.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* First Name */}
                  <div>
                    <label htmlFor="contact-first-name" className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-first-name"
                      placeholder="Enter your first name"
                      {...register("first_name", {
                        required: "First name is required",
                      })}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder:text-gray-400"
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-sm text-red-500">{errors.first_name.message}</p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label htmlFor="contact-last-name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-primary">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-last-name"
                      placeholder="Enter your last name"
                      {...register("last_name", {
                        required: "Last name is required",
                      })}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder:text-gray-400"
                    />
                    {errors.last_name && (
                      <p className="mt-1 text-sm text-red-500">{errors.last_name.message}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-primary">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      placeholder="your@email.com"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder:text-gray-400"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone <span className="text-primary">*</span>
                    </label>
                    <input
                      type="tel"
                      id="contact-phone"
                      placeholder={config.phone.placeholder}
                      {...register("phone", {
                        required: "Phone number is required",
                      })}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-900 placeholder:text-gray-400"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message <span className="text-primary">*</span>
                    </label>
                    <textarea
                      rows="4"
                      id="contact-message"
                      placeholder="Tell us about your project or inquiry..."
                      {...register("message", {
                        required: "Message is required",
                      })}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none transition-all text-gray-900 placeholder:text-gray-400"
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    className="group w-full px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center gap-3">
                      {loading ? (
                        <>
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                    {!loading && (
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                    )}
                  </motion.button>
                </form>

                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-tr-3xl rounded-bl-full -z-10"></div>
                <motion.div
                  animate={{
                    rotate: [0, 360],
                  }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute -bottom-4 -left-4 w-16 h-16 border-4 border-primary/10 -z-10"
                  style={{ transform: 'rotate(45deg)' }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;